import React, { useEffect, useRef } from 'react';
import { ChatMessage } from '~/features/chatbot/types/chatbot.types';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import WelcomeMessage from './WelcomeMessage';
import LoadingIndicator from './LoadingIndicator';

interface ConversationPanelProps {
    messages: ChatMessage[];
    isLoading: boolean;
    error: string | null;
    isNewSession: boolean;
    onSend: (text: string) => void;
    onQuestionClick: (question: string) => void;
    disabled: boolean;
}

const ConversationPanel = ({
    messages,
    isLoading,
    error,
    isNewSession,
    onSend,
    onQuestionClick,
    disabled,
}: ConversationPanelProps) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    const showWelcome = isNewSession && messages.length === 0;

    return (
        <div className="display-flex flex-column height-100pct" data-testid="conversation-panel">
            <div className="flex-1-1 overflow-y-auto padding-15">
                {showWelcome && <WelcomeMessage onQuestionClick={onQuestionClick} />}

                {messages.map((message) => (
                    <MessageBubble key={message.id} message={message} />
                ))}

                {isLoading && <LoadingIndicator />}

                {error && (
                    <div
                        className="alert alert-danger margin-top-10"
                        role="alert"
                        data-testid="conversation-error"
                    >
                        <span className="rioglyph rioglyph-error-sign margin-right-5" aria-hidden="true" />
                        {error}
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            <div className="padding-15 border-top-only">
                <MessageInput onSend={onSend} disabled={disabled} />
            </div>
        </div>
    );
};

export default ConversationPanel;
